<template>
  <div v-if="isCampaignEnded">
    <h2>Gedaan</h2>
  </div>
  <div v-else-if="isLoadingSignUpMoment" class="o-info-loading">
    <!-- This state prevents flickering signup states that may confuse the user about their current sign up state after a restart,
      especially when using a slower connection or during high load. -->
    <h2>Laden...</h2>
  </div>

  <div v-else-if="hasActiveSignupMoment && !subscribed">
    <div class="o-form">
      <h2 class="o-title--centered">{{ $t('signupMoment.title') }}</h2>
      <p>
        {{ $t('signupMoment.running.description') }}
      </p>
      <h1 v-if="isUsingSockets" class="o-timer">{{ duration }}</h1>
      <button :disabled="signUpState !== 'idle'" @click="handleSignup">
        <span v-if="signUpState === 'idle'"> {{ $t('signupMoment.running.btn') }}</span>
        <span v-if="signUpState === 'loading'">Laden...</span>
        <span v-if="signUpState === 'error'">Er is een fout opgetreden</span>
      </button>
      <div v-if="signUpState === 'error'" class="o-recovery-link">
        <a href="#" @click.prevent="resetAllStates">Problemen? Klik hier om opnieuw te proberen</a>
      </div>
      <OptIn v-if="config.optIn" v-model="consent" />
    </div>

    <div class="o-info">
      <p>
        {{ $t('campaign.extraInfo') }}
      </p>
    </div>
  </div>

  <div v-else-if="subscribed" class="o-subscribed">
    <img class="o-image--centered" src="../assets/ic_check.png" />
    <h2 class="o-title--centered">{{ $t('signupMoment.success.title', { moment: signedUpDate }) }}</h2>
    <p v-html="$t('signupMoment.success.description')" />
    <p v-html="$t('signupMoment.success.warning')" />
  </div>

  <div v-else class="o-form">
    <img class="o-image--centered" src="../assets/ic_time.png" />
    <h2 class="o-title--centered">{{ $t('signupMoment.preview.title') }}</h2>
    <p v-if="isCampaignLive">
      {{ $t('signupMoment.preview.description') }}
    </p>
    <p v-else>
      {{ $t('signupMoment.notRunning.description') }}
    </p>
  </div>

  <MobileConfirmationPopup v-if="showMobileConfirmation" @close="closeMobileConfirmation" @confirm="signUp" />
</template>

<script setup>
import { ref, computed } from 'vue'
import { isToday } from 'date-fns'
import { useSignupStore } from '../store/signup'
import { useUserStore } from '../store/user'
import { authentication } from '@dpgradio/creative'
import { CAMPAIGN_CONFIG } from '../utils/config'
import { pushConsent } from '../utils/consent'
import MobileConfirmationPopup from './MobileConfirmationPopup.vue'
import OptIn from './OptIn.vue'

const signupStore = useSignupStore()
const userStore = useUserStore()

const showMobileConfirmation = ref(false)
const signUpState = ref('idle')
const consent = ref(false)
const config = CAMPAIGN_CONFIG

const weekdayFormatter = new Intl.DateTimeFormat('nl-NL', { weekday: 'long' })

const signupMoment = computed(() => signupStore.signupMoment)
const subscribed = computed(() => signupStore.subscribed)
const hasActiveSignupMoment = computed(() => signupStore.hasActiveSignupMoment)
const shouldConfirmMobile = computed(() => userStore.shouldConfirmMobile)

const isLoadingSignUpMoment = computed(() => !signupMoment.value)

const isUsingSockets = computed(() => config.type === 'sockets')

const isCampaignLive = computed(() => {
  return 'campaignActive' in config ? config.campaignActive : true
})

const isCampaignEnded = computed(() => {
  return config.campaignEnded ?? false
})

const currentMomentDate = computed(() => {
  return signupMoment.value ? new Date(signupMoment.value.hiddenAt) : null
})

const signedUpDate = computed(() => {
  if (!currentMomentDate.value || isUsingSockets.value) {
    return 'het volgende spelmoment'
  } else if (isToday(currentMomentDate.value)) {
    return 'vandaag'
  } else {
    return weekdayFormatter.format(currentMomentDate.value)
  }
})

const duration = computed(() => {
  if (!signupMoment.value) return '00:00'
  const minutes = Math.floor((signupMoment.value.duration % 3600) / 60)
    .toString()
    .padStart(2, '0')
  const seconds = Math.floor(signupMoment.value.duration % 60)
    .toString()
    .padStart(2, '0')
  return `${minutes}:${seconds}`
})

const handleSignup = async () => {
  signUpState.value = 'loading'

  await authentication.require()

  try {
    await userStore.initConfirmationRequirement()

    if (shouldConfirmMobile.value) {
      showMobileConfirmation.value = true
    } else {
      await signUp()
    }
  } catch (error) {
    signUpState.value = 'error'
    throw error
  }

  if (consent.value) {
    const profile = await userStore.fetchProfile()
    await pushConsent(profile.email)
  }
}

const signUp = async () => {
  await signupStore.signUp()
  signUpState.value = 'idle'
}

const closeMobileConfirmation = () => {
  showMobileConfirmation.value = false
  signUpState.value = 'idle'
}

const resetAllStates = () => {
  signUpState.value = 'idle'
  showMobileConfirmation.value = false
  signupStore.isSigningUp = false
  signupStore.fetchCurrentSignUpMoment()
}
</script>

<style lang="scss" scoped>
.o-info-loading {
  background-color: rgb(var(--secondary-color));
  padding: 20px;
  color: white;
  text-align: center;
}

.o-form,
.o-subscribed {
  color: white;

  h1,
  h2 {
    font-family: var(--font-title);
    text-transform: uppercase;
    font-size: clamp(24px, 6cqw, 120px);
  }
}

.o-info {
  background-color: rgb(var(--primary-color));
  padding: 0px 20px 20px 20px;
  color: white;

  p {
    margin: 0;
  }
}

.o-title {
  &--centered {
    text-align: center;
  }
}

.o-image {
  &--centered {
    display: block;
    width: 64px;
    margin: 25px auto;
  }
}

.o-timer {
  text-align: center;
  font-size: clamp(48px, 15cqh, 100px);
}

button {
  font-family: var(--font-title);
  letter-spacing: var(--letter-spacing-title);
  text-transform: var(--title-text-transform);
  background-color: var(--c-sunny-yellow);
  color: rgb(var(--secondary-color));
  width: 100%;
  padding: 8px;
  font-size: 20px;
  font-weight: 600;
  border: 0;

  &:disabled {
    opacity: 0.5;
  }
}
.qmusic .o-form {
  background: none;
}

.qmusic .o-info {
  background: none;
}

.o-recovery-link {
  text-align: center;
  padding: 10px;

  a {
    color: white;
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }
}
p {
  text-align: center;
  font-size: clamp(12px, 5cqw, 48px);
}
</style>
